import React, { useState, useEffect, useCallback } from 'react';
import { 
    Loader2, Printer, RefreshCcw, ArrowLeft, 
    FileText, Calendar, Bed, ShoppingCart, DollarSign
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import API, { socket } from '../../services/api';

const RapportNightAudit = () => {
    const navigate = useNavigate();
    
    // --- ÉTATS ---
    const [loading, setLoading] = useState(false);
    const [auditData, setAuditData] = useState(null);
    const [dateCible, setDateCible] = useState(() => new Date().toISOString().split('T')[0]);

    // Récupération des données du Night Audit
    const fetchNightAudit = useCallback(async () => {
        setLoading(true);
        try {
            const res = await API.get('/manager/rapports/night-audit', { 
                params: { date_cible: dateCible } 
            });
            if (res.data.success) {
                setAuditData(res.data.data);
            }
        } catch (err) { 
            console.error("Erreur lors de la récupération du Night Audit:", err); 
        } finally { 
            setLoading(false); 
        }
    }, [dateCible]);

    useEffect(() => {
        fetchNightAudit();

        // 📡 CONFIGURATION TEMPS RÉEL (Socket)
        if (socket) {
            const handleRefresh = () => {
                fetchNightAudit();
            };

            socket.on('REFRESH_UI', handleRefresh);
            socket.on('DATA_EVENT', handleRefresh);

            return () => {
                socket.off('REFRESH_UI', handleRefresh);
                socket.off('DATA_EVENT', handleRefresh);
            };
        }
    }, [fetchNightAudit]);

    const formatCur = (v) => {
        if (v === undefined || v === null || isNaN(v) || v === 0) return "-";
        return Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 0 });
    };

    // --- FILTRAGE ET VÉRIFICATION DYNAMIQUE DES DONNÉES ---
    const occ = auditData?.occupation || {};
    const hasValidOccupation = Number(occ.total_chambres || 0) > 0 || Number(occ.chambres_occupees || 0) > 0;

    const ca = auditData?.chiffre_affaires || {};
    const caHebergement = Number(ca.hebergement || 0);
    const caRestauration = Number(ca.restauration || 0);
    const caExtras = Number(ca.extras || 0);
    const totalHotelier = Number(ca.total_hotelier || (caHebergement + caRestauration + caExtras));
    const hasValidCA = totalHotelier > 0 || caHebergement > 0 || caRestauration > 0 || caExtras > 0;

    const enc = auditData?.encaissements || {};
    const validEncModes = (enc.par_mode || []).filter(item => Number(item.total || 0) > 0);
    const totalEncaissements = Number(enc.total || 0);
    const hasValidEncaissements = totalEncaissements > 0 || validEncModes.length > 0;

    const synthese = auditData?.synthese || {};
    const ecartCaEnc = Number(synthese.ecart_ca_encaissement || 0);
    const hasValidSynthese = hasValidCA || hasValidEncaissements;

    return (
        <div style={s.layout}>
            <Sidebar />
            <main style={s.main}>
                <header style={s.header}>
                    <div style={s.headerLeft}>
                        <button onClick={() => navigate(-1)} style={s.btnBack}><ArrowLeft size={20} /></button>
                        <div style={s.titleContainer}>
                            <h2 style={s.title}>Rapport Night Audit & Synthèse</h2>
                            <div style={s.subtitleContainer}>
                                <span style={s.statusDot}></span>
                                <span style={s.subtitle}>Hôtel Chaine B • Audit Journalier</span>
                            </div>
                        </div>
                    </div>
                    <div style={s.headerRight}>
                        <button style={s.btnAction} onClick={fetchNightAudit}>
                            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCcw size={16} />} ACTUALISER
                        </button>
                        <button style={s.btnIconOnly} onClick={() => window.print()}><Printer size={20} /></button>
                    </div>
                </header>

                <section style={s.filterBar}>
                    <div style={s.filterGrid}>
                        <div style={s.fGroup}>
                            <label style={s.fLabel}>Date de l'Audit</label>
                            <div style={s.inputWrapper}>
                                <input 
                                    type="date" 
                                    style={s.fInput} 
                                    value={dateCible} 
                                    onChange={e => setDateCible(e.target.value)} 
                                />
                                <Calendar size={16} style={s.chevron}/>
                            </div>
                        </div>
                    </div>
                </section>

                <div style={s.tableArea}>
                    <div style={s.tableWrapper}>
                        {loading && !auditData ? (
                            <div style={s.load}><Loader2 className="animate-spin" size={50} color="#008000"/></div>
                        ) : auditData && (hasValidOccupation || hasValidCA || hasValidEncaissements) ? (
                            <table style={s.table}>
                                <thead style={s.thead}>
                                    <tr style={s.trHMain}>
                                        <th style={s.th}>INDICATEUR / SECTION</th>
                                        <th style={s.thR}>{dateCible}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* --- SECTION HÉBERGEMENTS & OCCUPATION (Masquée si vide) --- */}
                                    {hasValidOccupation && (
                                        <>
                                            <tr style={s.trSecH}>
                                                <td colSpan={2} style={s.tdSecT}><Bed size={16} style={{marginRight:8}}/> HÉBERGEMENTS & OCCUPATION</td>
                                            </tr>
                                            <tr style={s.trMain}>
                                                <td style={s.tD}>Nombre total de chambres</td>
                                                <td style={s.tD_Amt}>{occ.total_chambres}</td>
                                            </tr>
                                            <tr style={s.trMain}>
                                                <td style={s.tD}>Chambres occupées</td>
                                                <td style={s.tD_Amt}>{occ.chambres_occupees}</td>
                                            </tr>
                                            <tr style={s.trMain}>
                                                <td style={s.tD}>Chambres disponibles</td>
                                                <td style={s.tD_Amt}>{occ.chambres_disponibles}</td>
                                            </tr>
                                            {Number(occ.arrivees_prevues || 0) > 0 && (
                                                <tr style={s.trMain}>
                                                    <td style={s.tD}>Arrivées prévues</td>
                                                    <td style={s.tD_Amt}>{occ.arrivees_prevues}</td>
                                                </tr>
                                            )}
                                            {Number(occ.departs_attendus || 0) > 0 && (
                                                <tr style={s.trMain}>
                                                    <td style={s.tD}>Départs attendus</td>
                                                    <td style={s.tD_Amt}>{occ.departs_attendus}</td>
                                                </tr>
                                            )}
                                            {Number(occ.no_show || 0) > 0 && (
                                                <tr style={s.trMain}>
                                                    <td style={s.tD}>No-show</td>
                                                    <td style={s.tD_Amt}>{occ.no_show}</td>
                                                </tr>
                                            )}
                                            <tr style={s.trMain}>
                                                <td style={s.tD}>Taux d'Occupation (%)</td>
                                                <td style={s.tD_Amt}>{(Number(occ.taux_occupation_pourcent || 0)).toFixed(2)}%</td>
                                            </tr>
                                            {caHebergement > 0 && (
                                                <tr style={s.trTotSec}>
                                                    <td style={s.tdTotSecL}>C.A. Hébergement</td>
                                                    <td style={s.tdTotSecV}>{formatCur(caHebergement)}</td>
                                                </tr>
                                            )}
                                        </>
                                    )}

                                    {/* --- SECTION RESTAURATION & EXTRAS (Masquée si inexistante) --- */}
                                    {(caRestauration > 0 || caExtras > 0) && (
                                        <>
                                            <tr style={s.trSecH}>
                                                <td colSpan={2} style={s.tdSecT}><ShoppingCart size={16} style={{marginRight:8}}/> RESTAURATION & EXTRAS HÔTELIERS</td>
                                            </tr>
                                            {caRestauration > 0 && (
                                                <tr style={s.trMain}>
                                                    <td style={s.tD}>C.A. Restauration</td>
                                                    <td style={s.tD_Amt}>{formatCur(caRestauration)}</td>
                                                </tr>
                                            )}
                                            {caExtras > 0 && (
                                                <tr style={s.trMain}>
                                                    <td style={s.tD}>C.A. Extras & Services</td>
                                                    <td style={s.tD_Amt}>{formatCur(caExtras)}</td>
                                                </tr>
                                            )}
                                        </>
                                    )}

                                    {/* --- SECTION ENCAISSEMENTS (Masquée si vide) --- */}
                                    {hasValidEncaissements && (
                                        <>
                                            <tr style={s.trSecH}>
                                                <td colSpan={2} style={s.tdSecT}><DollarSign size={16} style={{marginRight:8}}/> ENCAISSEMENTS PAR MODE</td>
                                            </tr>
                                            {validEncModes.map((item, idx) => (
                                                <tr key={idx} style={s.trMain}>
                                                    <td style={s.tD}>{item.mode_paiement}</td>
                                                    <td style={s.tD_Amt}>{formatCur(item.total)}</td>
                                                </tr>
                                            ))}
                                            <tr style={s.trTotSec}>
                                                <td style={s.tdTotSecL}>TOTAL Encaissements du jour</td>
                                                <td style={s.tdTotSecV}>{formatCur(totalEncaissements)}</td>
                                            </tr>
                                        </>
                                    )}

                                    {/* --- SYNTHÈSE / ÉCART --- */}
                                    {hasValidSynthese && ecartCaEnc !== 0 && (
                                        <tr style={s.trMain}>
                                            <td style={s.tD}>Écart C.A. / Encaissements</td>
                                            <td style={{ ...s.tD_Amt, color: '#e11d48' }}>
                                                {formatCur(ecartCaEnc)}
                                            </td>
                                        </tr>
                                    )}

                                    {/* --- C.A. GLOBAL FINAL --- */}
                                    <tr style={s.trGlobal}>
                                        <td style={s.tdGlobalL}>C.A. HÔTELIER GLOBAL</td>
                                        <td style={s.tdGlobalV}>{formatCur(totalHotelier)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        ) : (
                            <div style={s.noData}><FileText size={40}/><p>Aucune donnée d'audit disponible pour cette date</p></div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

const s = {
    layout: { display: 'flex', height: '100vh', background: '#f8fafc' },
    main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    header: { background: '#008000', padding: '15px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white' },
    headerLeft: { display: 'flex', alignItems: 'center', gap: '20px' },
    titleContainer: { display: 'flex', flexDirection: 'column' },
    title: { margin: 0, fontSize: '18px', fontWeight: '900' },
    subtitleContainer: { display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' },
    statusDot: { width: '8px', height: '8px', background: '#4ade80', borderRadius: '50%' },
    subtitle: { fontSize: '11px', color: '#e8f5e9' },
    headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
    btnBack: { background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '10px', color: 'white', padding: '8px', cursor: 'pointer' },
    btnAction: { background: 'white', color: '#008000', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' },
    btnIconOnly: { background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', padding: '10px', borderRadius: '8px', cursor: 'pointer' },
    filterBar: { background: 'white', padding: '20px 40px', borderBottom: '1px solid #e2e8f0' },
    filterGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' },
    fGroup: { display: 'flex', flexDirection: 'column', gap: '4px', position: 'relative' },
    fLabel: { fontSize: '10px', fontWeight: 'bold', color: '#008000' },
    fInput: { padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px' },
    inputWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
    chevron: { position: 'absolute', right: '8px', color: '#64748b' },
    tableArea: { flex: 1, padding: '20px', overflow: 'hidden' },
    tableWrapper: { height: '100%', background: 'white', borderRadius: '10px', overflow: 'auto', border: '1px solid #e2e8f0' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
    thead: { position: 'sticky', top: 0, background: 'white', zIndex: 10 },
    trHMain: { borderBottom: '2px solid #008000' },
    th: { padding: '12px 20px', textAlign: 'left', color: '#008000', fontWeight: 'bold' },
    thR: { padding: '12px 20px', textAlign: 'right', color: '#008000', fontWeight: 'bold' },
    trSecH: { background: '#008000', color: 'white' },
    tdSecT: { padding: '10px 20px', fontWeight: 'bold', display: 'flex', alignItems: 'center' },
    trMain: { borderBottom: '1px solid #f1f5f9' },
    tD: { padding: '12px 20px' },
    tD_Amt: { textAlign: 'right', padding: '12px 20px', fontWeight: '600' },
    trTotSec: { background: '#f0fff4', borderTop: '2px solid #008000', fontWeight: 'bold' },
    tdTotSecL: { padding: '12px 20px', color: '#008000' },
    tdTotSecV: { textAlign: 'right', padding: '12px 20px', color: '#008000' },
    trGlobal: { background: '#000000', color: 'white', fontWeight: '900', borderTop: '4px solid #000' },
    tdGlobalL: { padding: '15px 20px', fontSize: '13px' },
    tdGlobalV: { textAlign: 'right', padding: '15px 20px', fontSize: '15px', color: '#ff5252' },
    load: { display: 'flex', justifyContent: 'center', padding: '50px' },
    noData: { textAlign: 'center', padding: '50px', color: '#94a3b8' }
};

export default RapportNightAudit;